<template>
  <div class="filter-color-swatches">
    <div class="grid grid-cols-5 gap-2">
      <div 
        v-for="option in filteredOptions" 
        :key="option.value"
        class="relative cursor-pointer"
        @click="toggleOption(option.value)"
      >
        <div 
          class="w-full aspect-square rounded-full border-2 flex items-center justify-center shadow-xs hover:shadow-md transition-shadow duration-200"
          :class="[
            isOptionSelected(option.value) 
              ? 'border-primary ring-2 ring-primary/40 shadow-md' 
              : 'border-transparent hover:border-muted-foreground/30',
            getBackgroundColorClass(option.value)
          ]"
          :style="{ backgroundColor: option.colorCode || getColorByName(option.value) }"
        >
          <CheckIcon 
            v-if="isOptionSelected(option.value)"
            class="h-5 w-5 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
          />
        </div>
        <div class="mt-1 text-center text-xs truncate">
          <span 
            class="font-medium" 
            :class="{ 'text-primary': isOptionSelected(option.value) }"
          >{{ option.displayValue }}</span>
          <span class="text-muted-foreground">({{ getOptionCount(option.value) }})</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { CheckIcon } from '@heroicons/vue/24/outline'
import { useFilters } from '~/layers/base/composables/useFilters'
import { useVehiclesStore } from '~/stores/vehicles'

const props = defineProps({
  filter: {
    type: Object,
    required: true
  },
  activeValue: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:value', 'clear'])

const { getFilterOptionCount } = useFilters()

// Get count of vehicles for a filter option
const getOptionCount = (optionValue: string): number => {
  if (!props.filter) return 0
  return getFilterOptionCount(props.filter.name, optionValue)
}

// Filter options to only show those with count > 0
const filteredOptions = computed(() => {
  if (!props.filter || !props.filter.data) {
    // Attempt to get the filter from the store directly
    const vehiclesStore = useVehiclesStore()
    const colourFilter = (vehiclesStore as any).getFilterByName('colour')
    
    if (colourFilter?.data && Array.isArray(colourFilter.data)) {
      return colourFilter.data
    }
    
    // If we still don't have data, try to extract it directly from vehicles
    const extractedColors = extractColorDataFromVehicles()
    if (extractedColors.length > 0) {
      return extractedColors
    }
    
    return getFallbackColorOptions()
  }
  
  if (!Array.isArray(props.filter.data)) {
    return getFallbackColorOptions()
  }
  
  // Use a Map to track unique color values to avoid duplicates
  const uniqueOptions = new Map()
  
  // Process each option, keeping only unique colors
  props.filter.data.forEach((option: any) => {
    if (!option.value) return
    
    const count = getOptionCount(option.value)
    const isSelected = isOptionSelected(option.value)
    
    // Only keep options with count > 0 or if they're selected
    if (count > 0 || isSelected) {
      // Use lowercase value as the key to prevent duplicates with different cases
      const key = option.value.toLowerCase()
      
      // Only add if not already in the map or if this one is selected
      if (!uniqueOptions.has(key) || isSelected) {
        uniqueOptions.set(key, option)
      }
    }
  })
  
  // Convert map values back to array
  const filtered = Array.from(uniqueOptions.values())
  
  // Provide fallback if no options are available
  if (filtered.length === 0) {
    return getFallbackColorOptions()
  }
  
  return filtered
})

// Get fallback color options
const getFallbackColorOptions = () => {
  return [
    { value: 'black', displayValue: 'Black', colorCode: '#000000' },
    { value: 'white', displayValue: 'White', colorCode: '#FFFFFF' },
    { value: 'silver', displayValue: 'Silver', colorCode: '#C0C0C0' },
    { value: 'blue', displayValue: 'Blue', colorCode: '#0000FF' },
    { value: 'red', displayValue: 'Red', colorCode: '#FF0000' },
    { value: 'grey', displayValue: 'Grey', colorCode: '#808080' },
    { value: 'green', displayValue: 'Green', colorCode: '#008000' },
    { value: 'yellow', displayValue: 'Yellow', colorCode: '#FFFF00' }
  ]
}

// Improved color mapping for common colors
const colorMap: Record<string, string> = {
  'black': '#000000',
  'white': '#FFFFFF',
  'silver': '#C0C0C0',
  'grey': '#808080',
  'blue': '#0000FF',
  'red': '#FF0000',
  'green': '#008000',
  'yellow': '#FFFF00',
  'orange': '#FFA500',
  'brown': '#A52A2A',
  'purple': '#800080',
  'gold': '#FFD700',
  'beige': '#F5F5DC',
  'maroon': '#800000',
  'gray': '#808080',   // Alternative spelling
  'cream': '#FFFDD0',
  'tan': '#D2B48C',
  'navy': '#000080',
  'burgundy': '#800020',
  'charcoal': '#36454F'
}

// Get color by name
const getColorByName = (colorName: string) => {
  return colorMap[colorName.toLowerCase()] || '#CCCCCC'
}

// Check if an option is selected
const isOptionSelected = (value: string) => {
  if (!Array.isArray(props.activeValue)) {
    return false;
  }
  
  // The value might be stored with a different case, so normalize for comparison
  const normalizedValue = value.toLowerCase();
  const normalizedActiveValues = props.activeValue.map((v: any) => String(v).toLowerCase());
  
  return normalizedActiveValues.includes(normalizedValue);
}

// Toggle an option selection
const toggleOption = (value: string) => {
  // Normalize the value for consistent handling
  const normalizedValue = value.toLowerCase();
  
  if (!Array.isArray(props.activeValue)) {
    emit('update:value', [normalizedValue]);
    return;
  }
  
  // Check if this color is already selected
  const isSelected = isOptionSelected(value);
  
  if (isSelected) {
    // Remove the value if it's already selected
    const newValue = props.activeValue.filter(
      (v: any) => String(v).toLowerCase() !== normalizedValue
    );
    
    // If the array is empty, emit undefined to clear the filter
    emit('update:value', newValue.length ? newValue : undefined);
  } else {
    // Add the value if it's not already selected
    const newValue = [...props.activeValue, normalizedValue];
    emit('update:value', newValue);
  }
}

// Get background color class based on the color
const getBackgroundColorClass = (color: string) => {
  const lowerColor = color.toLowerCase()
  
  // For very light colors, add a subtle gray background
  if (['white', 'cream', 'beige', 'silver'].includes(lowerColor)) {
    return 'bg-gray-50'
  }
  
  return ''
}

// Extract color data directly from vehicles
const extractColorDataFromVehicles = () => {
  const vehiclesStore = useVehiclesStore()
  
  if (!vehiclesStore.vehicles?.length) {
    return []
  }
  
  try {
    // Map to store unique colors
    const colorMap = new Map()
    
    // Extract colors from vehicles
    vehiclesStore.vehicles.forEach(vehicle => {
      if (vehicle?.colour?.value?.length) {
        const colorValues = vehicle.colour.value || [];
        const colorDisplayValues = vehicle.colour.displayValue || [];
        
        colorValues.forEach((color: string, index: number) => {
          const displayValue = colorDisplayValues[index] || capitalize(color);
          const normalizedColor = color.toLowerCase();
          
          if (!colorMap.has(normalizedColor)) {
            colorMap.set(normalizedColor, {
              value: normalizedColor,
              displayValue,
              colorCode: getColorByName(normalizedColor)
            });
          }
        });
      }
    })
    
    // Convert map to array and return
    return Array.from(colorMap.values())
  } catch (error) {
    return []
  }
}

// Simple capitalize function
const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}
</script>